// Legenda colori v2 — barra gradiente/step per il parametro attivo, costruita
// dal registro WSPalettes (riusato as-is dalla v1: stessi domain/stops, quindi
// stessi colori dei marker). In basso a sinistra, aggiornata al cambio parametro.
import { chartUnitForParam } from '../lib/params.js';
import { state as stationsState, isFutureHidden, isStationsFieldSuppressed } from '../layers/stations.js';

let box = null;
let currentParam = 'temperatura';
const contextual = new Map();
let renderedKey = null;
function setContent(key, html) {
    if (key === renderedKey) return;
    box.innerHTML = html;
    renderedKey = key;
}

function timelineDock() {
    return document.querySelector('.wsv2-ut');
}

function mountBox() {
    if (!box) return false;
    if (box.classList.contains('wsv2-legend--inline')) box.classList.remove('wsv2-legend--inline');
    if (box.parentElement !== document.body) document.body.appendChild(box);
    return true;
}

function paletteFor(parametro) {
    const P = window.WSPalettes;
    if (!P) return null;
    const rainWindow = /^rain(?:1|3|6|12|24|48|72|96|120)h$/.test(parametro);
    const key = rainWindow ? 'pioggia'
        : (P.paletteKeyForParam ? P.paletteKeyForParam(parametro) : parametro);
    const def = P.registry?.[key];
    if (!def) return null;
    return { key, def, scale: P.scaleForParam ? P.scaleForParam(rainWindow ? 'rain' : parametro) : P.scale(key) };
}

function render(parametro) {
    currentParam = parametro || currentParam;
    if (!box) {
        box = document.createElement('div');
        box.className = 'wsv2-legend';
    }
    if (!mountBox()) { box.hidden = true; return; }
    if (contextual.size) {
        box.hidden = false;
        box.classList.add('wsv2-legend--multi');
        box.classList.toggle('wsv2-legend--side', [...contextual.values()].some(spec=>spec.side));
        const key = 'context:' + JSON.stringify([...contextual.values()]);
        if (key !== renderedKey) setContent(key, [...contextual.values()].map(renderContextSpec).join('')); 
        return;
    }
    box.classList.remove('wsv2-legend--multi','wsv2-legend--side');
    // "Mostra direzione vento": i marker diventano banderuole colorate sulla
    // scala VENTO, non più sul parametro scelto → la legenda deve seguire ciò
    // che è davvero disegnato (prima restava quella del parametro, es. la barra
    // temperatura sotto banderuole vento: incoerente. Fix 2026-07-22).
    const windMode = !!stationsState.options.wind;
    const legendParam = windMode
        ? (currentParam === 'raffica' ? 'raffica' : 'vento')
        : currentParam;
    const pal = paletteFor(legendParam);
    if (!stationsState.options.visible || isFutureHidden() || isStationsFieldSuppressed()) {
        box.hidden = true;
        return;
    }
    if (legendParam === 'comfort') {
        box.hidden = false;
        setContent('comfort', '<strong>Comfort termico</strong><div class="wsv2-comfort-key"><span style="color:#21854d">● Comfort</span><span style="color:#a96808">● Moderato</span><span style="color:#c43d45">● Disagio</span></div><small>Stazioni aggiornate · stima all’ombra</small>');
        return;
    }
    if (!pal) { box.hidden = true; return; }
    const { def, scale } = pal;
    const unit = chartUnitForParam(legendParam) || '';
    const domain = def.domain || def.breaks || [];
    if (!domain.length) { box.hidden = true; return; }
    const renderKey = JSON.stringify([legendParam, windMode, unit, def]);
    box.hidden = false;
    if (renderKey === renderedKey) return;
    const min = domain[0];
    const max = domain[domain.length - 1];

    // Barra: campiono la scala a 48 step (fedele sia ai gradienti sia agli step).
    const stops = [];
    for (let i = 0; i <= 48; i++) {
        const v = min + (max - min) * (i / 48);
        let color;
        try { color = scale(v).hex ? scale(v).hex() : String(scale(v)); } catch (e) { color = '#888'; }
        stops.push(`${color} ${(i / 48 * 100).toFixed(1)}%`);
    }
    // Tick: 5 valori equidistanti.
    const ticks = [];
    for (let i = 0; i <= 4; i++) {
        const v = min + (max - min) * (i / 4);
        ticks.push(Math.abs(v) >= 100 ? Math.round(v) : Math.round(v * 10) / 10);
    }
    box.hidden = false;
    // In modalità vento intesta la barra (il colore non è più il parametro del
    // menu) e spiega i due simboli non-cromatici: asta = provenienza, ○ = calma.
    const head = windMode
        ? `<div class="wsv2-legend-title">${legendParam === 'raffica' ? 'Raffica' : 'Vento'} <small>direzione e intensità</small></div>`
        : '';
    const foot = windMode
        ? '<div class="wsv2-legend-note">Asta e sigla: provenienza · ○ calma</div>'
        : legendParam === 'vento' ? '<div class="wsv2-legend-note">Freccia e sigla: provenienza del vento</div>' : '';
    setContent(renderKey, `${head}
        <div class="wsv2-legend-bar" style="background:linear-gradient(to right, ${stops.join(',')})"></div>
        <div class="wsv2-legend-ticks">${ticks.map(t => `<span>${t}</span>`).join('')}</div>
        <div class="wsv2-legend-unit">${unit}</div>${foot}`);
}

function esc(s) {
    return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function renderContextSpec(spec) {
    const head = `<div class="wsv2-nl__title">${esc(spec.title)}${spec.unit ? ` <small>${esc(spec.unit)}</small>` : ''}</div>`;
    if (spec.side) {
        const gradient=spec.bar.map(s=>`${esc(s.color)} ${(s.pos*100).toFixed(2)}%`).join(',');
        return `<section class="wsv2-field-scale">${head}<div class="wsv2-field-scale__body"><div class="wsv2-field-scale__bar" style="background:linear-gradient(to top,${gradient})"></div><div class="wsv2-field-scale__ticks">${spec.ticks.map(t=>`<span style="bottom:${t.pos*100}%">${esc(t.label)}</span>`).join('')}</div></div><small>Accumulo · ${esc(spec.unit)}</small></section>`;
    }
    if (spec.steps) {
        const items = spec.steps.map(s =>
            `<span class="wsv2-nl__step"><i style="background:${esc(s.color)}"></i>${esc(s.label)}</span>`).join('');
        return `<div class="wsv2-nl__item">${head}<div class="wsv2-nl__steps">${items}</div></div>`;
    }
    const grad = (spec.bar || []).map(s => `${esc(s.color)} ${(s.pos * 100).toFixed(1)}%`).join(',');
    const ticks = (spec.ticks || []).map(t =>
        `<span style="left:${(t.pos * 100).toFixed(1)}%">${esc(t.label)}</span>`).join('');
    return `<div class="wsv2-nl__item">${head}
        <div class="wsv2-nl__bar" style="background:linear-gradient(to right, ${grad})"></div>
        <div class="wsv2-nl__ticks">${ticks}</div></div>`;
}

function setContextLegend(id, spec) {
    if (spec) contextual.set(id, spec);
    else contextual.delete(id);
    render(currentParam);
}

export function initLegend() {
    render(document.getElementById('selectParametro')?.value || 'temperatura');
    document.getElementById('selectParametro')?.addEventListener('change', (e) => render(e.target.value));
    const syncVisibility = () => render(currentParam);
    document.addEventListener('wsv2:stations-toggle', syncVisibility);
    document.addEventListener('wsv2:stations-mode', syncVisibility);
    document.addEventListener('wsv2:stations-visible', syncVisibility);
    document.addEventListener('wsv2:timeline-dock-mounted', (e) => {
        if (e.detail?.dock?.classList?.contains('wsv2-ut')) syncVisibility();
    });
    document.addEventListener('wsv2:map-legend', (e) => {
        const { id, spec } = e.detail || {};
        if (id) setContextLegend(id, spec || null);
    });
    // Traccia il parametro REALMENTE renderizzato (anche quando cambia via
    // deep-link/persist/setParametro, che non emettono 'change' sul <select>).
    document.addEventListener('wsv2:stations', (e) => {
        const p = e.detail?.parametro;
        if (p) render(p);
    });
}
